import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { AlertFlowsByKeywordQueryVariables, useAlertFlowsByKeywordQuery } from "../../graphql"
import { AlertFlowsList } from "./AlertFlowsList"

export type AlertFlowsByKeywordProps = {
	keyword: string
}

export const AlertFlowsByKeyword: React.FC<AlertFlowsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<AlertFlowsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useAlertFlowsByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.alertFlowsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.alertFlowsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading alert flows</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.alertFlowsByKeyword.alertFlows && data.alertFlowsByKeyword.alertFlows.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.alertFlowsByKeyword.alertFlows.length || 0} alert flow
						{(data.alertFlowsByKeyword.alertFlows.length || 0) > 1 ? "s" : ""}
					</Text>

					<AlertFlowsList alertFlows={data.alertFlowsByKeyword.alertFlows} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more alert flows</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any alert flows.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
